import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

export function LeadContactForm({
  propertyId,
  propertyTitle,
}: {
  propertyId: string;
  propertyTitle: string;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(
    `Hola, me interesa la propiedad "${propertyTitle}". ¿Podrías darme más información?`,
  );
  const [sending, setSending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      toast.error("Ingresá tu nombre y al menos un contacto (email o teléfono).");
      return;
    }
    setSending(true);
    try {
      const contactId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const { error: cErr } = await supabase.from("contacts").insert({
        id: contactId,
        full_name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        source: "formulario",
      });
      if (cErr) throw cErr;

      const { error: lErr } = await supabase.from("leads").insert({
        contact_id: contactId,
        property_id: propertyId,
        channel: "formulario",
        message: message.trim() || null,
        pipeline: "compradores",
        stage: "nuevo",
      });
      if (lErr) throw lErr;

      toast.success("¡Consulta enviada! Te contactaremos a la brevedad.");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo enviar";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Tu nombre"
        aria-label="Tu nombre"
        className={inputCls}
        required
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        aria-label="Email"
        className={inputCls}
      />
      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="WhatsApp / Teléfono"
        aria-label="WhatsApp o teléfono"
        className={inputCls}
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        aria-label="Mensaje"
        placeholder="Escribí tu consulta"
        className={`${inputCls} resize-none`}
      />
      <button
        type="submit"
        disabled={sending}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-soft transition hover:brightness-110 disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Enviar consulta
      </button>
    </form>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
